<template>
  <div class="asset-card-wrap">
    <v-card
      :disabled="disabled"
      @click="$emit('click')"
      @keyup.enter="$emit('click')"
      class="full-height"
      :class="{ 'has-warning': showWarning }">
      <v-carousel
        height="200"
        hide-delimiter-background
        :hide-delimiters="photos.length <= 1"
        show-arrows-on-hover
        :show-arrows="photos.length > 1"
        v-model="currentPhoto">
        <v-carousel-item
          :class="status === 'Churned' ? 'grayscale' : ''"
          v-for="photo in photos"
          :key="photo.id"
          :src="
            getSignedUrl
              ? getSignedUrl(photo.photoUrl)
              : photo.photoUrl
          "
          :lazy-src="lazyPlaceholder"></v-carousel-item>
      </v-carousel>
      <v-card-title>
        {{ description }}
        <EsWarrantyEligibilityChip v-if="isWarrantyEligible" mini class="ml-1" />
      </v-card-title>
      <v-card-subtitle>
        {{ getServiceCategoryName(categoryId) }}
      </v-card-subtitle>
      <v-card-text>
        <div>
          <span class="font-weight-bold">Type:</span>
          {{ type }}
        </div>
        <div>
          <span class="font-weight-bold">Manufacturer:</span>
          {{ manufacturer }}
        </div>
        <div>
          <span class="font-weight-bold">Model Number:</span>
          {{ modelNumber }}
        </div>
        <div>
          <span class="font-weight-bold">Serial Number:</span>
          {{ serialNumber }}
        </div>
        <div>
          <span class="font-weight-bold">Location:</span>
          {{ location }}
        </div>
        <div>
          <span class="font-weight-bold">Relative Location:</span>
          {{ relativeLocation }}
        </div>
        <div>
          <span class="font-weight-bold">Total Requests:</span>
          {{ totalRequests }}
        </div>
        <div>
          <span class="font-weight-bold">Spend Total:</span>
          {{ totalSpend }}
        </div>
        <div>
          <span :class="`${replacementCandidate ? 'red--text' : ''}`">
            <span class="font-weight-bold">% of spend:</span>
            {{ formattedPercentOfSpend }}
          </span>
        </div>
        <div v-show="lastRequestDate">
          <span class="font-weight-bold">Date of last request:</span>
          {{ lastRequestDate | MMDDYYYY }}
        </div>
        <div v-show="!lastRequestDate">
          <span class="font-weight-bold">Date of last request:</span>
          -
        </div>
        <div>
          <span class="font-weight-bold">Status:</span>
          {{ status }}
        </div>
      </v-card-text>
    </v-card>
    <EsButtonIcon
      icon="mdi-magnify"
      color="white"
      class="magnify-photo-button"
      @click="showZoom" />
    <v-dialog v-model="showAssetZoom" v-if="showAssetZoom" width="75%">
      <EsAssetZoom
        :photos="photos"
        :selectedPhoto="currentPhoto"
        :getSignedUrl="getSignedUrl" />
    </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import { EsButtonIcon } from '@86-repairs/design-system';
import EsAssetZoom from './EsAssetZoom.vue';
import EsWarrantyEligibilityChip from './EsWarrantyEligibilityChip.vue';
import placeholder from '@/assets/material.jpg';

dayjs.extend(utc);

export default {
  name: 'EsAssetCard',
  components: {
    EsButtonIcon,
    EsAssetZoom,
    EsWarrantyEligibilityChip,
  },
  props: {
    asset: {
      type: Object,
    },
    allServiceCategories: {
      type: Array,
      default: () => [],
      required: true,
    },
    getSignedUrl: {
      type: Function,
      required: false,
    },
    isSsp: {
      type: Boolean,
      default: false,
    },
    isPartsTown: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    currentPhoto: 0,
    showAssetZoom: false,
  }),
  filters: {
    MMDDYYYY: (value) => {
      if (!value) return '';
      return dayjs(value).utc().format('MM/DD/YYYY');
    },
  },
  computed: {
    lazyPlaceholder() {
      return this.isPartsTown ? placeholder : 'https://cdn.vuetifyjs.com/images/parallax/material.jpg';
    },
    description() {
      return this.asset?.description || 'Asset title';
    },
    status() {
      return this.asset?.assetStatus || 'Unknown';
    },
    categoryId() {
      return this.asset?.assetType?.serviceCategoryId || 'Category';
    },
    type() {
      return this.asset?.assetType?.name || 'Unknown';
    },
    manufacturer() {
      return this.asset?.manufacturer?.name || 'Unknown';
    },
    modelNumber() {
      return this.asset?.modelNumber || 'Unknown';
    },
    serialNumber() {
      return this.asset?.serialNumber || 'Unknown';
    },
    location() {
      return this.asset?.location?.name || 'Unknown';
    },
    relativeLocation() {
      return this.asset?.relativeLocation || 'Unknown';
    },
    lastRequestDate() {
      return this.asset?.lastRequestDate || null;
    },
    totalRequests() {
      return this.asset?.requestCount || 0;
    },
    photos() {
      let photos =
        this.asset?.assetPhotos?.filter(
          (photo) => photo.photoUrl && photo.photoUrl !== '',
        ) || [];
      photos.sort((a, b) => {
        const AIsPinned = this.asset?.pinnedAssetPhotoId === a.id;
        const BIsPinned = this.asset?.pinnedAssetPhotoId === b.id;

        const AIsUnitPhoto = a.photoType === 'Unit Photo';
        const BIsUnitPhoto = b.photoType === 'Unit Photo';

        return AIsPinned ? -1 : BIsPinned ? 1 : AIsUnitPhoto && !BIsUnitPhoto ? -1 : 0;
      });
      if (!photos || photos?.length < 1) {
        photos = [
          {
            id: 1,
            photoUrl:
              'https://via.placeholder.com/500x500.jpg?text=No+Photos',
          },
        ];
      }
      return photos;
    },
    totalSpend() {
      return this.asset?.totalSpend ? `$${this.asset?.totalSpend}` : '-';
    },
    percentOfSpend() {
      return this.asset?.percentOfSpend != null
        ? Math.floor(this.asset?.percentOfSpend * 100)
        : null;
    },
    formattedPercentOfSpend() {
      return `${this.percentOfSpend != null ? `${this.percentOfSpend}%` : '-'}`;
    },
    replacementCandidate() {
      return this.percentOfSpend >= 50;
    },
    totalRequestsLast90Days() {
      return this.asset?.requestCount90Days || 0;
    },
    showWarning() {
      return (
        this.isSsp &&
        (this.replacementCandidate || this.totalRequestsLast90Days > 3)
      );
    },
    isWarrantyEligible() {
      return this.asset?.unexpiredWarrantyPolicyCount > 0;
    },
  },
  mounted() {
    this.$nextTick(() => this.changeControlTabIndex());
  },
  methods: {
    changeControlTabIndex() {
      const arrows = document.querySelectorAll(
        'button.v-btn--flat.v-btn--icon.v-btn--round.theme--dark',
      );
      arrows.forEach((el) => {
        // eslint-disable-next-line no-param-reassign
        el.tabIndex = -1;
      });
    },
    getServiceCategoryName(id) {
      return this.allServiceCategories.find((c) => c.id === id)?.name;
    },
    showZoom() {
      this.showAssetZoom = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.v-card {
  cursor: pointer;
}
.v-card__text,
.v-card__title {
  word-break: normal;
}
.asset-card-wrap.pre-selected .v-card {
  border: 5px solid var(--v-accent-base);
  border-radius: 1px;
}
.full-height {
  height: 100%;
  &:focus {
    border: 5px solid var(--v-primary-base);
    border-radius: 1px;
  }
}
.has-warning {
  border: 1px solid var(--v-error-base);
}

.asset-card-wrap {
  position: relative;
}

.magnify-photo-button {
  z-index: 1;
  position: absolute;
  top: 0;
  right: 0;
}
.grayscale {
  filter: grayscale(1),
}
</style>
